import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, Alert, FormControl, InputLabel, Select, MenuItem, Tooltip, Paper } from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import { getProfile, updateProfile } from '../services/authApiService';
import { useAuth } from '../contexts/AuthContext';
import linesOverlay from '../assets/images/Lines-no_background.png';

const LANGUAGE_FLAGS = {
  en: '🇬🇧',
  fi: '🇫🇮',
  sv: '🇸🇪'
};

const SUPPORTED_LANGUAGES = [
  ['en', 'English'],
  ['fi', 'Suomi'],
  ['sv', 'Svenska']
];

const heroBoxStyles = {
  backgroundColor: '#021c2d',
  backgroundImage: `url(${linesOverlay})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  padding: { xs: 1.5, md: 2 },
  position: 'relative',
  zIndex: 1,
  borderRadius: '16px 16px 0 0'
};

const formContainerStyles = {
  padding: { xs: 3, md: 5 },
  backgroundColor: 'white',
  borderRadius: '0 0 16px 16px'
};

const Profile = () => {
  const { user, isDemoMode } = useAuth();
  const [profile, setProfile] = useState({ title: '', department: '', responsibilities: '', company: '', industry: '', org_id: null, organization_departments: [], language: 'en' });
  const [industries, setIndustries] = useState([]);
  const [industriesAvailable, setIndustriesAvailable] = useState(false);
  const [orgDepartments, setOrgDepartments] = useState([]);
  const [isOrgManaged, setIsOrgManaged] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          setLoading(true);
          const fetchedProfile = await getProfile();
          const profileWithDefaults = {
            title: fetchedProfile.title || '',
            department: fetchedProfile.department || '',
            responsibilities: fetchedProfile.responsibilities || '',
            company: fetchedProfile.company || '',
            industry: fetchedProfile.industry || '',
            org_id: fetchedProfile.org_id || null,
            organization_departments: fetchedProfile.organization_departments || [],
            language: fetchedProfile.language || 'en'
          };
          setProfile(profileWithDefaults);
          setLanguage(fetchedProfile.language || 'en');
          const managed = Boolean(profileWithDefaults.org_id);
          setIsOrgManaged(managed);
          const deptOptions = (profileWithDefaults.organization_departments || []).filter(Boolean);
          setOrgDepartments(deptOptions);
        } catch (error) {
          console.error("Failed to fetch profile", error);
        } finally {
          setLoading(false);
        }
      } else {
        console.warn('User not authenticated');
        setLoading(false);
      }
    };

    const fetchIndustries = async () => {
      if (!process.env.REACT_APP_API_URL) {
        console.error("REACT_APP_API_URL not configured - industries dropdown disabled");
        setIndustries([]);
        setIndustriesAvailable(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/config/industries`);
        const data = await response.json();
        setIndustries(data.industries || []);
        setIndustriesAvailable(data.available !== false);
      } catch (error) {
        console.error("Failed to fetch industries", error);
        setIndustries([]);
        setIndustriesAvailable(false);
      }
    };

    fetchProfile();
    fetchIndustries();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prevProfile) => ({
      ...prevProfile,
      [name]: value,
    }));
  };

  const handleLanguageSelect = (code) => {
    setLanguage(code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (user) {
      try {
        const profileToUpdate = { ...profile, language };
        if (isOrgManaged) {
          delete profileToUpdate.company;
          delete profileToUpdate.industry;
        }
        await updateProfile(profileToUpdate);

        // Show success notification
        setShowSuccess(true);

        // Auto-hide after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);

      } catch (error) {
        console.error('Failed to update profile:', error);
        alert('Failed to update profile.');
      }
    }
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Box sx={heroBoxStyles}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bebas Neue',
            letterSpacing: 1
          }}
        >
          Personal Profile
        </Typography>
      </Box>

      <Box sx={formContainerStyles}>
        {showSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setShowSuccess(false)}>
            Profile updated successfully!
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: '#0d1b2a'
            }}
          >
            Interview language
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {SUPPORTED_LANGUAGES.map(([code, name]) => (
              <Tooltip key={code} title={isDemoMode ? "Editing disabled in demo mode" : name} placement="top">
                <Box
                  onClick={() => !isDemoMode && handleLanguageSelect(code)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: isDemoMode ? 'not-allowed' : 'pointer',
                    padding: '4px 8px',
                    transition: 'transform 0.2s',
                    opacity: isDemoMode ? 0.6 : 1,
                    '&:hover': {
                      transform: isDemoMode ? 'none' : 'scale(1.05)'
                    }
                  }}
                >
                  <Box sx={{
                    fontSize: '1.4rem',
                    opacity: 1
                  }}>
                    {LANGUAGE_FLAGS[code] || '🌐'}
                  </Box>
                  <Box sx={{
                    width: '100%',
                    height: '2px',
                    backgroundColor: language === code ? '#021c2d' : 'transparent',
                    borderRadius: '1px',
                    marginTop: '2px',
                    transition: 'background-color 0.2s'
                  }} />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            value={user?.email || ''}
            disabled
            sx={{ mb: { xs: 2, sm: 2 }, backgroundColor: '#f5f5f5' }}
          />
          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <TextField
              label="Title"
              variant="outlined"
              fullWidth
              name="title"
              value={profile.title}
              onChange={handleChange}
              disabled={isDemoMode}
              sx={{ mb: { xs: 2, sm: 2 } }}
            />
          </Tooltip>
          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <TextField
              label="Company"
              variant="outlined"
              fullWidth
              name="company"
              value={profile.org_id === 'individual-users-pool' ? '' : profile.company}
              onChange={handleChange}
              placeholder={profile.org_id === 'individual-users-pool' ? 'Not available for personal account' : ''}
              InputProps={{ readOnly: isOrgManaged }}
              disabled={isDemoMode || isOrgManaged}
              sx={{
                mb: { xs: 2, sm: 2 },
                backgroundColor: (isOrgManaged || isDemoMode) ? '#f5f5f5' : 'inherit',
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)'
                }
              }}
            />
          </Tooltip>
          {orgDepartments.length > 0 ? (
            <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
              <FormControl fullWidth sx={{ mb: { xs: 2, sm: 2 } }}>
                <InputLabel id="department-label">Department / Team</InputLabel>
                <Select
                  labelId="department-label"
                  label="Department / Team"
                  name="department"
                  value={profile.department || ''}
                  onChange={handleChange}
                  disabled={isDemoMode}
                >
                  <MenuItem value="">
                    <em>Select Department / Team</em>
                  </MenuItem>
                  {orgDepartments.map((dept, index) => (
                    <MenuItem key={index} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          ) : (
            <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
              <TextField
                label="Department"
                variant="outlined"
                fullWidth
                name="department"
                value={profile.department}
                onChange={handleChange}
                disabled={isDemoMode}
                sx={{ mb: { xs: 2, sm: 2 } }}
              />
            </Tooltip>
          )}
          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <TextField
              label="Responsibilities"
              variant="outlined"
              fullWidth
              multiline
              rows={{ xs: 3, sm: 4 }}
              name="responsibilities"
              value={profile.responsibilities}
              onChange={handleChange}
              disabled={isDemoMode}
              sx={{ mb: { xs: 2, sm: 2 } }}
            />
          </Tooltip>
          {profile.org_id !== 'individual-users-pool' && (
            <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
              <FormControl fullWidth sx={{ mb: { xs: 2, sm: 2 } }}>
                <InputLabel id="industry-label">Industry</InputLabel>
                <Select
                  labelId="industry-label"
                  label="Industry"
                  name="industry"
                  value={profile.industry}
                  onChange={handleChange}
                  disabled={isDemoMode || isOrgManaged || !industriesAvailable}
                  sx={{
                    backgroundColor: (isOrgManaged || isDemoMode) ? '#f5f5f5' : 'inherit',
                    '& .MuiSelect-select.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)'
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Select an industry</em>
                  </MenuItem>
                  {industries.map((industry, index) => (
                    <MenuItem key={index} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          )}
          <Tooltip title={isDemoMode ? "Editing disabled in demo mode" : ""} placement="top">
            <span>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isDemoMode}
                sx={{
                  backgroundColor: '#021c2d',
                  '&:hover': { backgroundColor: '#042a44' },
                  textTransform: 'none',
                  fontSize: '1rem',
                  px: 4
                }}
              >
                Save Changes
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default Profile;
